import { TASK_REQUIRED_FIELDS, type Task } from "@/types/task";
import { TRIAL_REQUIRED_FIELDS, type Trial } from "@/types/trial";
import { assertCompleteDocument } from "@shared/validation/firestoreDocumentFields";
import type { DocumentReference, Timestamp } from "firebase/firestore";

/**
 * The values a document takes from its environment rather than from the
 * person creating it.
 *
 * `timestamp` is whatever the caller writes for `createdAt`/`updatedAt` — a
 * `serverTimestamp()` sentinel from the app, or a real `Timestamp` from a script
 * using the Admin SDK.
 */
export interface DocumentContext {
  orgId: string;
  timestamp: Timestamp;
  userRef: DocumentReference;
}

/**
 * Assembles a complete trial document, or throws.
 *
 * Build trials here rather than writing an object literal at the call site. A
 * literal that omits a required field is not a write error — Firestore runs with
 * `ignoreUndefinedProperties: true` and drops it — so the document is stored
 * looking fine and takes down the trials list for the whole organization the
 * next time anyone loads it. That is not hypothetical; it is what a hand-written
 * staging trial missing `startDate` did.
 *
 * Every field except `startDate` has a sensible empty default, so in practice
 * `startDate` is the one the caller must supply. It has no honest default:
 * stamping today's date would fabricate a research record's start.
 */
export const buildTrialDocument = (input: Partial<Trial>, ctx: DocumentContext): Trial =>
  assertCompleteDocument(
    {
      name: input.name || "New Trial",
      description: input.description ?? "",
      orgId: ctx.orgId,
      locationName: input.locationName ?? "",
      locations: input.locations ?? null,
      hasMultipleLocations: input.hasMultipleLocations ?? false,
      crops: input.crops ?? [],
      startDate: input.startDate,
      endDate: input.endDate ?? null,
      collaboratorIds: input.collaboratorIds ?? [],
      createdAt: ctx.timestamp,
      updatedAt: ctx.timestamp,
      createdBy: ctx.userRef,
      updatedBy: ctx.userRef,
    },
    TRIAL_REQUIRED_FIELDS,
    "trial"
  );

/**
 * Assembles a complete task document, or throws.
 *
 * `createTask` previously assigned `name`, `dataCollectionConfigs`, `state` and
 * `startDate` straight from caller input with no defaults, and its `Omit<Task,
 * "id">` annotation was erased by an `as any` at the only call site — so nothing
 * actually stopped an incomplete task from being written. A task without `state`
 * takes down the whole experiment route on read.
 *
 * `state` defaults to `"planned"`, which is an honest starting state. `startDate`
 * does not default, for the same reason it does not on a trial: inventing a date
 * on a research record is worse than refusing to write.
 */
export const buildTaskDocument = (input: Partial<Task>, ctx: DocumentContext): Omit<Task, "id"> =>
  assertCompleteDocument(
    {
      name: input.name || "New Task",
      description: input.description ?? "",
      dataCollectionConfigs: input.dataCollectionConfigs ?? [],
      state: input.state ?? "planned",
      startDate: input.startDate,
      endDate: input.endDate,
      metadata: input.metadata ?? {},
      createdAt: ctx.timestamp,
      updatedAt: ctx.timestamp,
      createdBy: ctx.userRef,
      updatedBy: ctx.userRef,
    },
    TASK_REQUIRED_FIELDS,
    "task"
  );
